import { Gym, HandledError, TimeSpan } from 'fitbeat.models';
import { INotificationService } from 'fitbeat.notification-service';
import { UserProfile } from '../../entities/user/userprofile';
import { WaitlistEntry } from '../../entities/waitlist';
import { DbGymRepository, IWaitinglistRepository } from '../../persistence/repositories';
import { IBookingsManager } from '../bookings/ibookingsmanager';
import { ITimeSlot } from '../gymscheduling/igymscheduling';
import { IWaitlist, IWaitlistUpdateStatus } from './iwaitlist';

export class Waitlist implements IWaitlist {
  constructor(
    private waitinglistRepository: IWaitinglistRepository,
    private bookingsManager: IBookingsManager,
  ) {
    bookingsManager.setWaitList(this);
  }

  public async getWaitinglistEntriesForUser(user: UserProfile): Promise<WaitlistEntry[]> {
    return this.waitinglistRepository.getAllForUser(user.uid);
  }

  public async addToWaitingList(
    user: UserProfile,
    timeSlot: ITimeSlot,
    gym: Gym,
    dateOfRequest: Date = new Date(),
  ): Promise<WaitlistEntry> {
    if (timeSlot.isDoubleSlotForPartner && !gym.singleStationPerZoneGym) {
      throw new HandledError('Cannot be placed on the waitinglist for partner bookings');
    }

    const targetSlot = await this.bookingsManager.getTargetTimeSlot(
      user,
      timeSlot.isDoubleSlotForPartner,
      timeSlot.workoutType,
      new Date(timeSlot.startTime),
      dateOfRequest,
      gym,
    );

    if (!targetSlot) {
      throw new HandledError('No such slot found');
    }

    const existingWaitlistEntry = await this.getWaitlistEntry(user, targetSlot, gym.name);
    if (existingWaitlistEntry) {
      return existingWaitlistEntry;
    }
    const timeSlots = await this.bookingsManager.GetAvailableBookingSlots(
      targetSlot.isDoubleSlotForPartner,
      user,
      gym,
      TimeSpan.createStartOfDayDate(new Date(targetSlot.startTime)),
      undefined,
      dateOfRequest,
    );

    if (timeSlots.some((slot) => slot.booked)) {
      throw new HandledError('Cancel your existing booking before joining a waitlist');
    }

    await this.removeAllInRange(user, timeSlots, gym.name);
    const entry = new WaitlistEntry(targetSlot, user, gym.name);
    await this.waitinglistRepository.add(entry);
    return entry;
  }

  public async removeFromWaitingList(user: UserProfile, waitlistEntry: WaitlistEntry): Promise<WaitlistEntry> {
    if (user.uid !== waitlistEntry.userProfile.uid) {
      throw new HandledError('Specified waitlistEntry does not belong to specified user');
    }
    return this.waitinglistRepository.remove(waitlistEntry);
  }

  public async isInWaitingList(user: UserProfile, timeSlot: ITimeSlot, gymName: string): Promise<boolean> {
    return (!!await this.getWaitlistEntry(user, timeSlot, gymName));
  }

  public async getWaitinglistEntriesForTimeslot(timeSlot: ITimeSlot): Promise<WaitlistEntry[]> {
    return this.waitinglistRepository.getAllForTimeslot(timeSlot);
  }

  public async getWaitlistEntry(
    user: UserProfile,
    timeSlot: ITimeSlot,
    gymName: string,
  ): Promise<WaitlistEntry | undefined> {
    return this.waitinglistRepository.get(WaitlistEntry.GenerateUID(timeSlot, user.uid, gymName));
  }

  public async getUserWaitlistEntryById(userId: string, waitingListId: string): Promise<WaitlistEntry | undefined> {
    const waitingListEntry = await this.waitinglistRepository.get(waitingListId);

    if (waitingListEntry) {
      return waitingListEntry.userProfile.uid === userId ? waitingListEntry : undefined;
    }

    return waitingListEntry;
  }

  public async removeAllInRange(user: UserProfile, timeSlots: ITimeSlot[], gymName: string): Promise<WaitlistEntry[]> {
    const removed: WaitlistEntry[] = [];
    await Promise.all(timeSlots.map(async (slot: ITimeSlot): Promise<void> => {
      const entry = await this.getWaitlistEntry(user, slot, gymName);
      if (entry) {
        await this.removeFromWaitingList(user, entry);
        removed.push(entry);
      }
    }));
    return removed;
  }

  public async updateAllBasedOnCurrentAvailability(
    notificationService: INotificationService,
    date: Date,
  ): Promise<IWaitlistUpdateStatus> {
    const result: IWaitlistUpdateStatus = {
      waitlistSize: 0,
      errors: 0,
      slotWasFullButOpenedUp: 0,
      slotWasOpenIsNowTaken: 0,
      slotAlreadyStartedSoRemoved: 0,
      slotStateUnchanged: 0,
    };

    const waitlist = await this.waitinglistRepository.getAllEndingInFuture(date);
    const promises = waitlist.map(async (entry) => {
      try {
        const slot = entry.timeSlot;
        const key = `${slot.startTime}-${slot.endTime}-${slot.workoutType}-${entry.gymName}`;
        const gym = DbGymRepository.getByName(entry.gymName);
        const availabilities = await this.getAllTimeSlotAvailabilities(waitlist, date, gym);
        const value = availabilities.get(key);
        if (value instanceof Error) {
          throw value;
        } else if (value === undefined) {
          throw new Error('failed to get availability');
        }

        const isNowFull = value;
        await this.updateWaitlistEntryBasedOnNewState(entry, isNowFull, result, date, notificationService);
      } catch (e) {
        result.errors += 1;
      }
    });
    await Promise.all(promises);

    result.waitlistSize = waitlist.length;
    return result;
  }

  private async getAllTimeSlotAvailabilities(
    waitlist: WaitlistEntry[],
    date: Date,
    gym: Gym,
  ): Promise<Map<string, boolean | Error>> {
    const availabilities = new Map<string, boolean | Error>();
    const pending: Array<Promise<void>> = [];
    const uniqueTimeSlots = this.getUniqueTimeslots(waitlist);

    const calculateNewStateAndCache = async (key: string, slot: ITimeSlot) => {
      try {
        const isNowFull = await this.getNewTimeSlotAvailability(slot, date, gym);
        availabilities.set(key, isNowFull);
      } catch (err) {
        availabilities.set(key, err);
      }
    };

    for (const [key, slot] of uniqueTimeSlots.entries()) {
      const task = calculateNewStateAndCache(key, slot);
      pending.push(task);
    }

    await Promise.all(pending);
    return availabilities;
  }

  private getUniqueTimeslots(waitlist: WaitlistEntry[]): Map<string, ITimeSlot> {
    const uniqueTimeSlots = new Map<string, ITimeSlot>();
    const timeslots = waitlist.map((entry) => ({ timeSlot: entry.timeSlot, gymName: entry.gymName }));
    for (const { timeSlot, gymName } of timeslots) {
      const key = `${timeSlot.startTime}-${timeSlot.endTime}-${timeSlot.workoutType}-${gymName}`;
      uniqueTimeSlots.set(key, timeSlot);
    }
    return uniqueTimeSlots;
  }

  private async getNewTimeSlotAvailability(slot: ITimeSlot, date: Date, gym: Gym): Promise<boolean> {
    const start = new Date(slot.startTime);
    const end = new Date(slot.endTime);

    const amountOfOpenSlots = await this.bookingsManager.getAmountOfOpenSlotsAtPoint(
      start,
      end,
      date,
      gym,
      slot.workoutType,
      slot.isDoubleSlotForPartner,
    );
    return amountOfOpenSlots === 0;
  }

  private async updateWaitlistEntryBasedOnNewState(
    entry: WaitlistEntry,
    isNowFull: boolean,
    result: IWaitlistUpdateStatus,
    date: Date,
    notificationService: INotificationService,
  ): Promise<void> {
    const slot = entry.timeSlot;
    const slotAlreadyStarted = slot.startTime < date.getTime();
    if (slotAlreadyStarted) {
      result.slotAlreadyStartedSoRemoved += 1;
      await this.waitinglistRepository.remove(entry);
      return;
    }

    const slotWasFullButOpenedUp = slot.isFull && !isNowFull;
    const slotWasOpenIsNowTaken = !slot.isFull && isNowFull;
    const gym = DbGymRepository.getByName(entry.gymName);
    const gymTimeZone = gym.timezone;
    if (slotWasFullButOpenedUp) {
      await entry.notifyOfOpenSlot(date, gymTimeZone, notificationService);
      result.slotWasFullButOpenedUp += 1;
    } else if (slotWasOpenIsNowTaken) {
      await entry.notifyThatAllSlotsHaveBeenTaken(date, gymTimeZone, notificationService);
      result.slotWasOpenIsNowTaken += 1;
    } else {
      result.slotStateUnchanged += 1;
      return;
    }

    slot.isFull = isNowFull;
    await this.waitinglistRepository.update(entry);
  }
}
